import { isCompanySanteAtRisk, libelleSante } from './companySante.js'
import { libelleHorairesNuit } from '@shared/quote-intra-location.js'

/**
 * CE QUE LE COMMERCIAL DOIT AVOIR EN TÊTE AVANT DE CHIFFRER (retour Figma du
 * 2026-10-05, « Éléments à prendre en compte pour le devis ») : une entreprise
 * en mauvaise santé, des horaires de nuit, une formation le week-end. Trois
 * faits que l'analyse a déjà recueillis, chacun dans sa section ; le Point de
 * vigilance les rassemble au moment d'éditer le devis.
 *
 * LA SANTÉ SUIT LA RÈGLE DE LA FICHE (`QuoteDetailAlerts`) : seule une
 * entreprise compte, et « non récupérée » n'est pas un risque.
 *
 * LE WEEK-END NE SE LIT QUE SUR DES JOURS PRÉCIS. Une période « septembre
 * 2026 » ou des dates inconnues ne disent rien du jour de la semaine : rien
 * n'est annoncé plutôt qu'une supposition.
 *
 * @param {Object|null} quote - La demande telle qu'affichée (snake_case).
 * @returns {Array<{ key: string, icon: string, texte: string }>}
 */
export function elementsAPrendreEnCompte(quote) {
  if (!quote) return []
  return [santeRisquee(quote), horairesDeNuit(quote), joursDeWeekEnd(quote)].filter(Boolean)
}

function santeRisquee(quote) {
  if (quote.client_type !== 'professionnel' || !isCompanySanteAtRisk(quote.company_sante)) return null
  return {
    key: 'sante',
    icon: 'fas fa-heart-crack',
    texte: `Entreprise en mauvaise santé : ${libelleSante(quote.company_sante, quote.company_alertes)}`
  }
}

/** En INTER, les horaires sont ceux de la session : le champ n'y a pas de sens. */
function horairesDeNuit(quote) {
  if (quote.formation_lieu === 'INTER') return null
  const texte = libelleHorairesNuit({
    horairesNuit: quote.horaires_nuit,
    nombreGroupes: quote.nombre_groupes,
    groupesConcernes: quote.horaires_nuit_nombre_groupes
  })
  return texte ? { key: 'nuit', icon: 'fas fa-moon', texte } : null
}

function joursDeWeekEnd(quote) {
  const jours = (Array.isArray(quote.dates_disponibilites) ? quote.dates_disponibilites : [])
    .map(jourLocal)
    .filter((jour) => jour && (jour.getDay() === 0 || jour.getDay() === 6))
    .sort((a, b) => a - b)
  if (!jours.length) return null
  const dates = jours.map((jour) => jour.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }))
  return { key: 'weekend', icon: 'fas fa-calendar-week', texte: `Formation le week-end : ${dates.join(', ')}` }
}

/**
 * « 2026-09-26 » lu à minuit LOCAL : `new Date('2026-09-26')` le lit en UTC,
 * ce qui fait reculer d'un jour à l'ouest de Greenwich et rendrait le samedi
 * vendredi.
 */
function jourLocal(valeur) {
  const texte = valeur instanceof Date ? null : String(valeur || '')
  const match = texte && texte.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  const date = valeur instanceof Date ? valeur : null
  return date && !Number.isNaN(date.getTime()) ? date : null
}
